import Close from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import parse from 'html-react-parser';
import React from 'react';
import Faq from 'src/logic/models/catalogue/Faq';
import EmptySection from '../catalogue-content/components/EmptySection';
import ErrorSection from '../catalogue-content/components/ErrorSection';
import LoadingSection from '../catalogue-content/components/LoadingSection';

type FaqPopupProps = {
  open: boolean;
  onClose: () => void;
  queryKey: unknown[];
  fetchFaqs: () => Promise<{ data: Faq[] }>;
};

const FaqPopup = ({ open, onClose, queryKey, fetchFaqs }: FaqPopupProps) => {
  // undefined = no user interaction yet → default to first item
  // null      = user explicitly collapsed all
  // number    = user opened a specific item
  const [userSelectedId, setUserSelectedId] = React.useState<number | null | undefined>(undefined);

  const {
    data: faqs,
    isLoading,
    isError,
    refetch,
  } = useQuery<Faq[]>({
    queryKey,
    queryFn: async () => {
      const { data } = await fetchFaqs();
      return data;
    },
    enabled: open,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 0,
  });

  const expandedId = userSelectedId === undefined ? faqs?.[0]?.id ?? false : userSelectedId ?? false;

  React.useEffect(() => {
    if (!open) setUserSelectedId(undefined);
  }, [open, ...queryKey]);

  const handleAccordionChange = (faqId: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setUserSelectedId(isExpanded ? faqId : null);
  };

  const renderContent = () => {
    if (isLoading) return <LoadingSection label='Loading FAQs...' minHeight='200px' />;

    if (isError) return <ErrorSection refetch={refetch} minHeight='200px' />;

    if (!faqs || faqs.length === 0) {
      return <EmptySection hideQuestion text='No FAQs available' />;
    }

    return faqs.map((faq, index) => (
      <Accordion
        key={faq.id}
        expanded={expandedId === faq.id}
        onChange={handleAccordionChange(faq.id)}
        disableGutters
        square
        elevation={0}
        sx={{
          borderBottom: '1px solid #EBEBEB',
          boxShadow: 'none',
          '&::before': { display: 'none' },
          '&.Mui-expanded': { backgroundColor: '#F8F9FC' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#8B8D98' }} />}
          sx={{ px: 3, py: 1.5, minHeight: 'unset', '& .MuiAccordionSummary-content': { my: 0 } }}
        >
          <Typography fontWeight={500} fontSize={14} color='#3A3E50'>
            {index + 1}. {faq.question}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 3, pt: 0, pb: 2 }}>
          <Typography variant='body2' component='div' color='#8B8D98' fontSize={13} lineHeight={1.6}>
            {parse(faq.answer)}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { width: '560px', maxWidth: '560px', borderRadius: '12px' } }}
    >
      <DialogTitle
        component='div'
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px solid #EBEBEB',
        }}
      >
        <Typography fontWeight={700} fontSize={18} color='#3A3E50'>
          FAQ
        </Typography>
        <IconButton onClick={onClose} size='small' aria-label='Close FAQ'>
          <Close sx={{ color: '#8B8D98', fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 0, py: 0, maxHeight: '60vh', overflowY: 'auto' }}>{renderContent()}</DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #EBEBEB' }}>
        <Button variant='contained' color='primary' onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaqPopup;
